<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <div class="element-box mt-4">
            <div class="row mt-4">
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>
              <CoachingButton
                  :enabled="companySettings?.socioTherapyCoachingEnabled"
                  route-name="socioTherapyCoachingNewAndActiveList"
                  coaching-type="socioTherapy"
                  label="Soziotherapie"/>
              <CoachingButton
                  :enabled="companySettings?.assistedLivingCoachingEnabled"
                  route-name="assistedLivingCoachingNewAndActiveList"
                  coaching-type="assistedLiving"
                  label="Betreutes Wohnen"/>
              <CoachingButton
                  :enabled="companySettings?.caseManagementCoachingEnabled"
                  route-name="caseManagementCoachingNewAndActiveList"
                  coaching-type="caseManagement"
                  label="DRV Fallmanagement"/>
              <CoachingButton
                  :enabled="companySettings?.privateLearningCoachingEnabled"
                  route-name="privateLearningCoachingNewAndActiveList"
                  coaching-type="privateLearning"
                  label="Private Weiterbildung"/>
            </div>
          </div>

          <div class="row mt-5">
            <div class="col-lg-3 col-md-3 col-sm-12">
              <h6>Standortinformationen</h6>
              <div class="row g-4 mt-1">
                <div class="col">
                  <CoachingCardInfo
                      label="Einzelcoaching"
                      type="private"
                      :value="activeCoachingData?.counts?.privateCoaching"
                  />
                </div>
              </div>
            </div>

            <div class="col-lg-9 col-md-9 col-sm-12">
              <h6>{{ $t('dashboard.customer_information') }}</h6>
              <div class="row g-4 mt-1">
                <div class="col-md-4 col-sm-12">
                  <CoachingCardInfo
                      label="Kunden insgesamt"
                      type="default"
                      :value="totalCoaching"
                  />
                </div>
                <div class="col-md-4 col-sm-12">
                  <CoachingCardInfo
                      :label="$t('general.new_customers')"
                      type="newCustomers"
                      :value="newCoachingData?.total"
                  />
                </div>
                <div class="col-md-4 col-sm-12">
                  <CoachingCardInfo
                      :label="$t('general.active_customers')"
                      type="activeCustomers"
                      :value="activeCoachingData?.total"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="element-box mt-4">
            <div class="row align-items-center justify-content-between">
              <h6 class="mt-3 mb-0"> Standortinformationen</h6>
              <div class="form-group mb-0 col-12 col-md-4 mt-3 mt-md-0">
                <input name="searchTerm"
                       class="form-control"
                       @input="handleDebounceSearch"
                       v-model="searchTerm"
                       placeholder="Standort suchen"/>
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-sm-12">
                <div class="table-responsive">
                  <table class="table table-striped">
                    <thead>
                    <tr>
                      <th>Standort</th>
                      <th style="text-align: right">Aktive Kunden</th>
                      <th style="text-align: right">Neu Kunden</th>
                      <th style="text-align: right">geleistete Einheiten {{ currentMonth }}</th>
                      <th style="text-align: right">geva</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="location of locationsData" v-bind:key="location?.locationName">
                      <td>
                        {{ location?.locationName }}
                      </td>
                      <td style="text-align: right">
                        {{ location?.countActiveCoaching }}
                      </td>
                      <td style="text-align: right">
                        {{ location?.countNewCoaching }}
                      </td>
                      <td style="text-align: right">
                        {{ location?.unitsDone }}
                      </td>
                      <td style="text-align: right">
                        {{ location?.countGeva }}
                      </td>
                    </tr>
                    <tr v-if="!locationsData?.length">
                      <td colspan="5" class="text-center">Keine Standorte gefunden</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <Pagination
                    v-if="(pagination?.total_pages ?? 0) > 1"
                    :totalPages="pagination?.total_pages"
                    :currentPage="pagination?.current_page"
                    :totalItems="pagination?.total_items"
                    :perPage="pagination?.items_per_page"
                    @pagechanged="getInfoData"/>
              </div>
            </div>
          </div>

        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts
              :key="seriesActiveCoaching.length"
              width="100%"
              type="donut"
              :options="chartOptionsActive"
              :series="seriesActiveCoaching"/>
        </div>

        <div class="element-box">
          <h6>{{ $t('general.new_customers') }}</h6>
          <VueApexCharts
              :key="seriesNewCoaching.length"
              width="100%"
              type="donut"
              :options="chartOptionsNew"
              :series="seriesNewCoaching"/>
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardSiteSupportService from '@/api/Dashboard/SiteSupport';
import CoachingButton from '@/components/button/CoachingButton.vue';
import CoachingCardInfo from '@/components/card/CoachingCardInfo.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import Pagination from '@/components/Pagination.vue';
import {ErrorToast} from '@/components/VueToast';
import {optionsChartDonut} from '@/shared/utils/apex';
import {piniaCompanySettingsStore} from '@/store/piniaCompanySettingsStore';
import {piniaUserStore} from '@/store/piniaUserStore';
import debounce from 'lodash/debounce';
import {computed, onMounted, ref} from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import {initAppIfAuthenticated} from "@/shared/functions";

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const chartOptionsActive = ref(optionsChartDonut);
const chartOptionsNew = ref(optionsChartDonut);
const seriesActiveCoaching = ref([]);
const seriesNewCoaching = ref([]);
const activeCoachingData = ref(null);
const newCoachingData = ref(null)
const locationsData = ref([])
const pagination = ref(null)
const currentMonth = ref('')
const searchTerm = ref('')

const debouncedSearch = debounce(async () => {
  await getInfoData(1);
}, 600);

const handleDebounceSearch = () => {
  debouncedSearch();
};

// Series and labels must use the same order
const coachingTypes = [
  {key: 'privateCoaching', label: 'Einzelcoaching'},
  {key: 'groupCoaching', label: 'Gruppenmaßnahmen'},
  {key: 'socioTherapyCoaching', label: 'Soziotherapie'},
  {key: 'privateLearningCoaching', label: 'Private Weiterbildung'},
  {key: 'assistedLivingCoaching', label: 'Betreutes Wohnen'},
  {key: 'caseManagementCoaching', label: 'DRV Fallmanagement'},
];

const coachingSeries = (coachingData) => coachingTypes.map(type => coachingData?.counts?.[type.key] ?? 0);
const coachingLabels = (coachingData) => coachingTypes.map(type => `${type.label} ${coachingData?.counts?.[type.key] ?? 0}`);

async function getInfoData(page = 1) {
  try {

    const {data} = await dashboardSiteSupportService.getInfoData({
      page: page,
      limit: 25,
      searchTerm: searchTerm.value,
    });

    locationsData.value = data.data.locationsData;
    pagination.value = data.data.pagination;
    currentMonth.value = data.data.currentMonth;

    activeCoachingData.value = data.data?.activeCoachingData;
    seriesActiveCoaching.value = coachingSeries(activeCoachingData.value);

    chartOptionsActive.value = {
      ...chartOptionsActive.value,
      labels: coachingLabels(activeCoachingData.value),
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                formatter: () => activeCoachingData.value?.total.toString(),
              },
            },
          },
        },
      }
    };

    newCoachingData.value = data.data?.newCoachingData;
    seriesNewCoaching.value = coachingSeries(newCoachingData.value);

    chartOptionsNew.value = {
      ...chartOptionsNew.value,
      labels: coachingLabels(newCoachingData.value),
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                formatter: () => newCoachingData.value?.total.toString(),
              },
            },
          },
        },
      }
    };

  } catch (error) {
    await ErrorToast(error);
  }
}

const totalCoaching = computed(() => {
  return (newCoachingData.value?.total ?? 0) + (activeCoachingData.value?.total ?? 0);
});


onMounted(async () => {
  await initAppIfAuthenticated()
  await getInfoData();
});
</script>

<style scoped>

::v-deep(.apexcharts-legend) {
  display: block !important;
}

</style>